import React, { useEffect, useId, useState } from 'react';
import { ClockCircleTwoTone, WarningFilled } from '@ant-design/icons';
import { Alert, Button, Card, Flex, Progress, Spin, Typography, theme } from 'antd';

import config from '../../../assets/config';
import { usePrerenderUser } from '../../../hooks/usePrerenderUser';
import CloudflareNameserverPair from './CloudflareNameserverPair';
import CloudflareStepHeader from './CloudflareStepHeader';

const { Paragraph, Text } = Typography;

export const SCHEDULED_CHECK_INTERVAL_MS = config.cfPropagationBarMinutes * 60_000;

/** Long enough to read "Checking now…" when the check at the end of the bar answers at once. */
export const CHECKING_MIN_VISIBLE_MS = 800;

export const SCREEN_CLOCK_TICK_MS = 1_000;

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// FIXME: no exact token — the design's 420px status box.
const STATUS_BOX_WIDTH = 420;

const UPDATE_NAMESERVERS_GUIDE_URL =
  'https://developers.cloudflare.com/fundamentals/manage-domains/add-site/#3-update-nameservers';

/**
 * "3 minutes ago", from the server's timestamp.
 *
 * Never from the client clock: a machine whose clock is off by an hour would
 * otherwise be told its last check was an hour ago, or in the future.
 */
export const describeLastChecked = (
  lastCheckedAt: string | null,
  now: number,
): string => {
  if (!lastCheckedAt) return 'Not checked yet';

  const elapsedMs = now - new Date(lastCheckedAt).getTime();
  if (elapsedMs < 60_000) return 'Last checked: just now';

  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 60) return `Last checked: ${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.floor(minutes / 60);

  return `Last checked: ${hours} hour${hours === 1 ? '' : 's'} ago`;
};

const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.ceil(ms / 1000);

  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
};

export type CloudflarePropagationStepProps = {
  domain: string;
  lastCheckedAt: string | null;
  nameservers?: readonly string[];
  /**
   * Checks the zone now. Resolves `true` when the flow moved on (the screen is
   * about to leave), `false` when it is still waiting. `inBackground` keeps a
   * check the customer did not ask for from showing its errors.
   */
  onCheck?: (options: { inBackground: boolean }) => Promise<boolean>;
};

/**
 * `waiting` and `checkingAtEnd` show the bar; `warned` and `checkingAgain` show
 * the warning with Check again (Figma 4713-7122 and 5457-6623).
 */
type PropagationPhase = 'waiting' | 'checkingAtEnd' | 'warned' | 'checkingAgain';

/**
 * The wait after a nameserver change.
 *
 * The screen checks when it opens and again when its bar runs out; a check
 * that finds the zone active moves the flow on, and one that does not leaves
 * the bar running, or, at the end of the bar, the warning and Check again. Check
 * again checks at once and starts the bar over.
 *
 * `propagation_timed_out` lands here too, and looks the same: the 48-hour cutoff
 * stops the sweep, not the flow, so a customer who fixes their nameservers on
 * day four still completes.
 */
const CloudflarePropagationStep = ({ lastCheckedAt, nameservers = [], onCheck }: CloudflarePropagationStepProps) => {
  const { token } = theme.useToken();
  const { email } = usePrerenderUser();
  const [phase, setPhase] = useState<PropagationPhase>('waiting');
  const [now, setNow] = useState(() => Date.now());
  const [barStartedAt, setBarStartedAt] = useState(() => Date.now());
  const checkingNowId = useId();

  const checkZone = async (inBackground: boolean): Promise<boolean> =>
    (await onCheck?.({ inBackground })) ?? false;

  const startTheBarOver = () => {
    setBarStartedAt(Date.now());
    setNow(Date.now());
    setPhase('waiting');
  };

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), SCREEN_CLOCK_TICK_MS);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    checkZone(true);
  }, []);

  const untilNextCheck = Math.max(0, barStartedAt + SCHEDULED_CHECK_INTERVAL_MS - now);
  const barRanOut = phase === 'waiting' && untilNextCheck === 0;

  useEffect(() => {
    if (barRanOut) setPhase('checkingAtEnd');
  }, [barRanOut]);

  const checkingAtEnd = phase === 'checkingAtEnd';

  useEffect(() => {
    if (!checkingAtEnd) return undefined;

    let cancelled = false;
    Promise.all([checkZone(true), delay(CHECKING_MIN_VISIBLE_MS)]).then(([movedOn]) => {
      if (cancelled) return;

      if (movedOn) startTheBarOver();
      else setPhase('warned');
    });

    return () => {
      cancelled = true;
    };
  }, [checkingAtEnd]);

  const checkAgain = async () => {
    if (phase !== 'warned') return;

    setPhase('checkingAgain');
    if (await checkZone(false)) return;

    startTheBarOver();
  };

  const warned = phase === 'warned' || phase === 'checkingAgain';
  const hintStyle: React.CSSProperties = {
    marginBottom: 0,
    textAlign: 'center',
    fontSize: token.fontSizeSM,
    color: token.colorTextQuaternary,
  };

  return (
    <Flex vertical gap={token.marginLG}>
      <CloudflareStepHeader
        title="Waiting for DNS propagation"
        description="We're monitoring your nameserver changes."
      />

      <Card styles={{ body: { padding: token.paddingXL } }}>
        <Flex vertical align="center" gap={token.margin}>
          <CloudflareNameserverPair nameservers={nameservers} withRegistrarInstruction />
          <Paragraph style={hintStyle}>
            Not sure how to update your DNS settings?{' '}
            <Typography.Link
              href={UPDATE_NAMESERVERS_GUIDE_URL}
              target="_blank"
              underline
              style={{ fontSize: 'inherit', color: token.colorPrimary }}
            >
              Learn how it works
            </Typography.Link>
          </Paragraph>

          {warned ? (
            <>
              <Alert
                type="warning"
                showIcon
                icon={<WarningFilled />}
                style={{
                  width: '100%',
                  maxWidth: STATUS_BOX_WIDTH,
                  background: token.colorBgContainer,
                  borderColor: token.colorWarning,
                }}
                message="Public DNS still shows your old nameservers"
                description="If you haven't replaced them in your registrar yet, do it now with the pair above. If you already have, there is nothing to do, the change can take up to 48 hours to show."
              />
              <Button
                color="primary"
                variant="outlined"
                size="large"
                onClick={checkAgain}
                loading={phase === 'checkingAgain'}
              >
                Check again
              </Button>
            </>
          ) : (
            <>
              <Paragraph style={hintStyle}>
                You can close this page. We&apos;ll notify you on{' '}
                <Text strong style={{ fontSize: 'inherit', color: 'inherit' }}>{email}</Text> when it&apos;s live.
                <br />
                This typically takes 1-2 hours, but can take up to 48 hours.
              </Paragraph>

              {checkingAtEnd ? (
                <Flex
                  key="checking"
                  role="status"
                  aria-labelledby={checkingNowId}
                  aria-busy
                  justify="center"
                  align="center"
                  gap={token.marginXS}
                >
                  <Spin size="small" />
                  <Text id={checkingNowId}>Checking now…</Text>
                </Flex>
              ) : (
                <Flex
                  key="countdown"
                  vertical
                  gap={token.marginXS}
                  style={{
                    width: '100%',
                    maxWidth: STATUS_BOX_WIDTH,
                    padding: token.padding,
                    background: token.colorFillAlter,
                    border: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadiusLG,
                  }}
                >
                  <Flex justify="space-between" align="center" gap={token.marginXS}>
                    <Flex align="center" gap={token.marginXS}>
                      <ClockCircleTwoTone twoToneColor={token.colorPrimary} style={{ fontSize: token.fontSizeLG }} />
                      <Text>{describeLastChecked(lastCheckedAt, now)}</Text>
                    </Flex>
                    <Text>Next check in: {formatCountdown(untilNextCheck)}</Text>
                  </Flex>
                  <Progress
                    percent={((SCHEDULED_CHECK_INTERVAL_MS - untilNextCheck) / SCHEDULED_CHECK_INTERVAL_MS) * 100}
                    showInfo={false}
                    style={{ margin: 0 }}
                  />
                </Flex>
              )}
            </>
          )}
        </Flex>
      </Card>
    </Flex>
  );
};

export default CloudflarePropagationStep;
